<template>
  <div class="flex items-center gap-4">
    <button
      class="btn btn-xs"
      @click="clickTryAudio"
      :disabled="!text || audioLoading"
    >
      <template v-if="audioLoading">
        <div class="loading loading-dots loading-sm"></div>
      </template>
      <template v-else>
        <i class="iconify mingcute--play-circle-line"></i>
        {{ $t(`Buttons.${toneType}`) }}

        <PowerIcon />
        {{ powerCount }}
      </template>
    </button>

    <audio
      v-show="audioUrl"
      ref="audioPlayer"
      :src="audioUrl"
      class="h-8 flex-1"
      controls
      @ended="audioStatus = 'ended'"
      @loadstart="audioStatus = 'loading'"
      @pause="audioStatus = 'paused'"
      @playing="audioStatus = 'playing'"
      crossorigin="anonymous"
    />
  </div>
</template>

<script setup lang="ts">
import api from "@/modules/api";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { MateToneConfig, Media } from "@/types/entity";
import { computed, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    mateToneId: number;
    text: string;
    mateToneConfig?: MateToneConfig;
    toneType?: "voice" | "tryListen";
    activeMedia?: Media;
  }>(),
  {
    toneType: "tryListen",
  }
);

const powerCount = computed(() => {
  const len = props.text.length;
  if (len <= 200) return 1;
  return 1 + Number((Math.ceil((len - 200) / 10) * 0.1).toFixed(1));
});

const emit = defineEmits<{
  (e: "generate", media: Media): void;
}>();

watch(
  () => props.activeMedia,
  (newVal) => {
    audioUrl.value = newVal?.url || "";
  }
);

const audioStatus = ref("paused");

const audioPlayer = ref<HTMLAudioElement | null>(null);
const audioUrl = ref("");
const userStore = useGlobalUser();
const modalStore = useGlobalModal();
const audioLoading = ref(false);
const clickTryAudio = async () => {
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: powerCount.value,
      cb: clickTryAudio,
    })
  )
    return;

  audioLoading.value = true;
  const res = await api.playTextAudio({
    mateToneId: props.mateToneId,
    text: props.text,
    mateToneConfig: props.mateToneConfig,
  });
  emit("generate", res.item.media);
  userStore.refresh();

  audioUrl.value = res.item.media.url;
  await audioPlayer.value!.load();
  await audioPlayer.value!.play();
  audioLoading.value = false;
};

onMounted(() => {
  if (props.activeMedia) {
    audioUrl.value = props.activeMedia.url;
  }
});
</script>
